import CAButton from '@ds/components/buttons/CAButton'
import CAInput from '@ds/components/form-elements/CAInput'
import CAAlert from '@ds/components/notifications/CAAlert'
import api from '@vuejs/services/api'
import { getLoginMethodForEmail, signInWithPassword } from '@vuejs/services/signIn'
import {
	computed,
	defineComponent,
	onMounted,
	type Ref,
	ref,
	Transition,
} from 'vue'

export default defineComponent({
	name: 'SignInForm',

	setup() {
		const authMethod: Ref<'email_required' | 'keycloak' | 'password' | null> = ref(null)
		const csrfToken = ref('')
		const email = ref('')
		const errorMessage = ref('')
		const isCheckingAuthMethod = ref(false)
		const isLoading = ref(false)
		const keycloakFormRef = ref<HTMLFormElement | null>(null)
		const password = ref('')
		const showPassword = ref(false)

		// Extract error message from query params or data attribute (e.g., after failed Keycloak auth or password auth)
		onMounted(() => {
			// Always get CSRF token from meta tag (needed for sign-in retry after errors)
			const csrfElement = document.querySelector('meta[name="csrf-token"]')

			csrfToken.value = csrfElement?.getAttribute('content') || ''

			// First check for error from data attribute (failed password auth)
			const signInApp = document.getElementById('signInApp')
			const dataError = signInApp?.getAttribute('data-auth-error')

			if (dataError) {
				errorMessage.value = dataError

				return
			}

			// Then check URL params (failed Keycloak auth)
			const params = new URLSearchParams(window.location.search)
			const urlError = params.get('error')

			if (urlError) {
				errorMessage.value = decodeURIComponent(urlError)
			}
		})

		// The input phase covers both the initial state and the email-required state, where
		// the user keeps editing the field (e.g. switching from phone to email) before retrying.
		const isAwaitingInput = computed(
			() => authMethod.value === null || authMethod.value === 'email_required'
		)

		const showEmailRequiredNotice = computed(() => authMethod.value === 'email_required')

		const canCheckAuthMethod = computed(() => {
			return email.value.trim().length > 0 && isAwaitingInput.value
		})

		const keycloakLoginHint = computed(() => email.value.trim())

		const forgotPasswordUrl = computed(() => {
			const trimmedEmail = email.value.trim()

			if (!trimmedEmail) {
				return '/users/password/new'
			}

			// cspell:ignore Blogin
			return `/users/password/new?user%5Blogin%5D=${encodeURIComponent(trimmedEmail)}`
		})

		const showPasswordStep = computed(() => authMethod.value === 'password')

		// Both email and phone input go through the backend so it can detect the SSO-admin
		// phone-login edge case and return 'email_required'.
		const checkAuthMethod = async () => {
			isCheckingAuthMethod.value = true
			errorMessage.value = ''

			try {
				const trimmedEmail = email.value.trim()
				const response = await getLoginMethodForEmail(api, trimmedEmail)

				authMethod.value = response.auth_method

				if (response.auth_method === 'keycloak') {
					// Auto-submit the keycloak form — keep loading state on until the page redirects
					setTimeout(() => {
						keycloakFormRef.value?.submit()
					}, 0)

					return
				}
			} catch (error) {
				// If API call fails, fall back to password
				authMethod.value = 'password'
				console.error('Failed to determine login method for email:', error)
			} finally {
				isCheckingAuthMethod.value = false
			}
		}

		const handleSignIn = async () => {
			isLoading.value = true
			errorMessage.value = ''

			try {
				const response = await signInWithPassword(
					email.value.trim(),
					password.value,
					csrfToken.value
				)

				// Successful login - redirect
				if (response.redirect_url) {
					window.location.href = response.redirect_url
				}
			} catch (error) {
				// Failed login - show error message
				errorMessage.value = error instanceof Error ? error.message : 'Invalid email or password'
				isLoading.value = false
			}
		}

		const togglePasswordVisibility = () => {
			showPassword.value = !showPassword.value
		}

		return () => {
			const renderErrorMessage = errorMessage.value && <CAAlert
				dataTest="error-message"
				type="danger"
			>
				{errorMessage.value}
			</CAAlert>

			const renderEmailRequiredNotice = showEmailRequiredNotice.value && <CAAlert
				dataTest="email-required-notice"
				title="Email sign-in required"
				type="help"
			>
				This account requires email authentication. Please sign in with your email address to continue.
			</CAAlert>

			const renderContinueButton = (isAwaitingInput.value || isCheckingAuthMethod.value) && <CAButton
				class="account-page__button"
				dataTest="continue-button"
				disabled={!canCheckAuthMethod.value || isCheckingAuthMethod.value}
				loading={isCheckingAuthMethod.value}
				size="full"
				onClick={checkAuthMethod}
			>
				Continue
			</CAButton>

			return (
				<div class="account-page__input-group">
					{renderErrorMessage}

					{renderEmailRequiredNotice}

					{/* Email/phone input step - always visible */}
					<div aria-labelledby="emailOrPhoneInput" class="ca-form-control" role="group">
						<div class="ca-input__title-label">
							<label class="ca-label ca-label__with-icon" for="emailOrPhoneInput" id="emailOrPhoneInput-label">
								Email or mobile number
							</label>
						</div>
						<div class="ca-input__icon-input">
							<CAInput
								aria-describedby="emailOrPhoneInput-text"
								aria-label="Email or phone number"
								aria-labelledby="emailOrPhoneInput-label"
								dataTest="email-input"
								debounceDelay={0}
								disabled={!isAwaitingInput.value || isCheckingAuthMethod.value}
								icon="SVGIdentification"
								id="emailOrPhoneInput"
								inputMode="text"
								modelValue={email.value}
								placeholder="Email or mobile number"
								type="text"
								onEnter={checkAuthMethod}
								onUpdate:modelValue={(value: string) => {
									email.value = value
									errorMessage.value = ''
									authMethod.value = null
								}}
							/>

							{renderContinueButton}
						</div>
					</div>

					{/* Password step - revealed with transition */}
					<Transition name="sign-in-reveal">
						{showPasswordStep.value && <div aria-labelledby="passwordInput" class="ca-form-control" role="group">
							<div class="ca-input__title-label">
								<label class="ca-label ca-label__with-icon" for="passwordInput" id="passwordInput-label">
									Password
								</label>
							</div>
							<div class="ca-input__icon-input">
								<CAInput
									aria-describedby="passwordInput-message"
									aria-label="Password"
									aria-labelledby="passwordInput-label"
									dataTest="password-input"
									debounceDelay={0}
									disabled={isLoading.value}
									icon="SVGLock"
									iconHoverEffect={true}
									iconRight="SVGEye"
									id="passwordInput"
									inputMode="text"
									modelValue={password.value}
									placeholder="Password"
									type={showPassword.value ? 'text' : 'password'}
									onEnter={handleSignIn}
									onIconRightClick={togglePasswordVisibility}
									onUpdate:modelValue={(value: string) => {
										password.value = value
									}}
								/>
							</div>
						</div>}
					</Transition>

					<div class="account-page__input-container account-page__input-container--mobile-small">
						{showPasswordStep.value && <a
							href={forgotPasswordUrl.value}
							class="account-page__forgot-password"
							data-test="reset-password-button"
						>
							Forgot password
						</a>}
					</div>

					{showPasswordStep.value && <CAButton
						class="account-page__button"
						dataTest="sign-in-button"
						disabled={!password.value || isLoading.value}
						loading={isLoading.value}
						size="full"
						onClick={handleSignIn}
					>
						Sign in
					</CAButton>}

					{showPasswordStep.value && <div class="account-page__legal-notice">
						<span>By clicking "Sign in", you agree to our&nbsp;</span>
						<a
							href="https://careacademy.com/terms-of-service"
							class="account-page__legal-link"
							rel="noopener noreferrer"
							target="_blank"
						>
							terms of service
						</a>
						<span>&nbsp;and&nbsp;</span>
						<a
							href="https://careacademy.com/privacy-policy"
							class="account-page__legal-link"
							rel="noopener noreferrer"
							target="_blank"
						>
							privacy policy
						</a>
						<span>.</span>
					</div>}

					{/* Hidden Keycloak form - auto-submitted */}
					<form
						ref={keycloakFormRef}
						action="/users/auth/keycloak_openid"
						data-test="keycloak-form"
						method="POST"
						style={{ display: 'none' }}
					>
						<input
							name="authenticity_token"
							type="hidden"
							value={csrfToken.value}
						/>
						<input
							name="login_hint"
							type="hidden"
							value={keycloakLoginHint.value}
						/>
					</form>
				</div>
			)
		}
	},
})
